/**
 * Phosphor persistence.
 *
 * A ping-pong accumulator: `mix(prev, cur, 1 - persistence)`, with the retained half run
 * through a per-channel decay first. Real P22 phosphor does not fade neutrally — green
 * outlives red, red outlives blue — so a white trail drifts toward its own hue on the
 * way out instead of turning grey.
 *
 * The composite takes `max(scene, trail)`, so the current frame is never dimmed by its
 * own history and the trail can only ever add light. That matters for §11.9: a telegraph
 * edge cannot be darkened by a bright thing that used to be in front of it.
 *
 * Off at §14.6 rung 6 (AMD-2).
 */

import { GLSL_COLOR } from './common';

export const PERSISTENCE_FRAG = /* glsl */ `
precision highp float;

uniform sampler2D tCurrent;
uniform sampler2D tPrev;
uniform float uPersistence;
uniform vec3 uChromaDecay;
uniform float uTrailSaturation;
uniform float uFloor;

in vec2 vUv;
out vec4 fragColor;

${GLSL_COLOR}

void main() {
  vec3 cur = max(texture(tCurrent, vUv).rgb, vec3(0.0));
  vec3 prev = max(texture(tPrev, vUv).rgb, vec3(0.0));

  prev *= uChromaDecay;

  // Push the hue out as the trail loses energy: white core, coloured wake.
  float l = luma(prev);
  prev = max(mix(vec3(l), prev, uTrailSaturation), vec3(0.0));

  // Half-float denormals sit at 1e-7 forever otherwise, which shows up as a permanent
  // smudge wherever anything bright has ever been.
  prev *= smoothstep(uFloor * 0.25, uFloor, luma(prev));

  fragColor = vec4(mix(prev, cur, 1.0 - uPersistence), 1.0);
}
`;
